// src/services/indexedDB/core/database.ts
// 資料庫初始化和配置

import { logError } from '../../../utils/logger';

/**
 * 資料庫配置常量
 */
export const DB_NAME = 'OrloLocalDB';
export const DB_VERSION = 9;

/**
 * Store 名稱常量
 */
export const STORE_NAMES = {
  TIME_BOXES: 'timeBoxes',
  EVENTS: 'events',
  TASKS: 'tasks',
  CAPTURES: 'captures',
  OBJECTIVES: 'objectives',
  SYNC_QUEUE: 'syncQueue',
} as const;

/**
 * 資料庫實例快取（單例模式）
 */
let dbInstance: IDBDatabase | null = null;

/**
 * 初始化 IndexedDB 資料庫
 * @returns Promise<IDBDatabase>
 */
export const initDB = (): Promise<IDBDatabase> => {
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;

      // 創建 TimeBoxes 存儲
      if (!db.objectStoreNames.contains(STORE_NAMES.TIME_BOXES)) {
        const store = db.createObjectStore(STORE_NAMES.TIME_BOXES, { keyPath: 'id' });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('userId_date', ['userId', 'dateString'], { unique: false });
      }

      // 創建 Events 存儲
      if (!db.objectStoreNames.contains(STORE_NAMES.EVENTS)) {
        const eventStore = db.createObjectStore(STORE_NAMES.EVENTS, { keyPath: 'id' });
        eventStore.createIndex('userId', 'userId', { unique: false });
        eventStore.createIndex('userId_date', ['userId', 'dateString'], { unique: false });
      }

      // 創建 Tasks 存儲
      if (!db.objectStoreNames.contains(STORE_NAMES.TASKS)) {
        const taskStore = db.createObjectStore(STORE_NAMES.TASKS, { keyPath: 'id' });
        taskStore.createIndex('userId', 'userId', { unique: false });
        taskStore.createIndex('userId_status', ['userId', 'status'], { unique: false });
        taskStore.createIndex('userId_createdAt', ['userId', 'createdAt'], { unique: false });
        taskStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        taskStore.createIndex('userId_cachedAt', ['userId', 'cachedAt'], { unique: false });
        taskStore.createIndex('userId_objectiveId', ['userId', 'objectiveId'], { unique: false });
      }

      // v9 升級：為現有 Tasks 存儲新增 userId_objectiveId 索引
      if (event.oldVersion < 9 && db.objectStoreNames.contains(STORE_NAMES.TASKS)) {
        const taskStore = request.transaction!.objectStore(STORE_NAMES.TASKS);
        if (!taskStore.indexNames.contains('userId_objectiveId')) {
          taskStore.createIndex('userId_objectiveId', ['userId', 'objectiveId'], { unique: false });
        }
      }

      // 創建 Captures 存儲
      if (!db.objectStoreNames.contains(STORE_NAMES.CAPTURES)) {
        const captureStore = db.createObjectStore(STORE_NAMES.CAPTURES, { keyPath: 'id' });
        captureStore.createIndex('userId', 'userId', { unique: false });
        captureStore.createIndex('userId_processedNum', ['userId', 'processedNum'], { unique: false });
        captureStore.createIndex('userId_createdAt', ['userId', 'createdAt'], { unique: false });
        captureStore.createIndex('userId_timeBoxId', ['userId', 'timeBoxId'], { unique: false });
        captureStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        captureStore.createIndex('userId_cachedAt', ['userId', 'cachedAt'], { unique: false });
      }

      // 創建 Objectives 存儲
      if (!db.objectStoreNames.contains(STORE_NAMES.OBJECTIVES)) {
        const objectiveStore = db.createObjectStore(STORE_NAMES.OBJECTIVES, { keyPath: 'id' });
        objectiveStore.createIndex('userId', 'userId', { unique: false });
        objectiveStore.createIndex('userId_status', ['userId', 'status'], { unique: false });
        objectiveStore.createIndex('userId_updatedAt', ['userId', 'updatedAt'], { unique: false });
        objectiveStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        objectiveStore.createIndex('userId_syncStatus', ['userId', 'syncStatus'], { unique: false });
      }

      // 創建同步佇列存儲
      if (!db.objectStoreNames.contains(STORE_NAMES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORE_NAMES.SYNC_QUEUE, { keyPath: 'id' });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('operation', 'operation', { unique: false });
        syncStore.createIndex('captureId', 'captureId', { unique: false });
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;

      // 連線被外部關閉（如清空 IndexedDB）或版本變更時，清除快取避免使用失效連線
      dbInstance.onclose = () => { dbInstance = null; };
      dbInstance.onversionchange = () => {
        dbInstance?.close();
        dbInstance = null;
      };

      resolve(dbInstance);
    };

    request.onerror = () => {
      logError('Failed to initialize IndexedDB', request.error);
      reject(request.error);
    };
  });
};

/**
 * 關閉資料庫連接
 */
export const closeDB = (): void => {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
};

/**
 * 刪除整個資料庫（測試用）
 */
export const deleteDB = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    closeDB();
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};
